import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-19">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 10,
      scale: 0.97
    }} transition={{
      duration: 0.3
    }} className="bg-[#FAF6F0] text-[#2D2D2D] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-20">
        {/* Header */}
        <div className="sticky top-0 bg-[#1B2141] text-[#FAF6F0] px-6 py-5 rounded-t-2xl flex items-center justify-between" data-id="element-21">
          <h2 className="font-serif text-2xl" data-id="element-22">
            {type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#FAF6F0]/10 transition-colors" aria-label="Cerrar" data-id="element-23">
            <X className="w-5 h-5" data-id="element-24" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-8 text-base leading-relaxed space-y-6" data-id="element-25">
          {type === 'privacy' ? <PrivacyContent data-id="element-26" /> : <TermsContent data-id="element-27" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-sm text-[#2D2D2D]/60" data-id="element-28">
        Última actualización: febrero 2026
      </p>

      <div className="space-y-4" data-id="element-29">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-30">
          1. Información que recopilamos
        </h3>
        <p data-id="element-31">
          Cuando realizas una compra en El Club de las 3:00 AM, recopilamos
          únicamente la información necesaria para procesar tu pedido: nombre,
          dirección de correo electrónico y datos de pago. Los datos de pago son
          procesados de forma segura por nuestro proveedor de pagos y no
          almacenamos información de tarjetas de crédito.
        </p>
      </div>

      <div className="space-y-4" data-id="element-32">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-33">
          2. Uso de la información
        </h3>
        <p data-id="element-34">Utilizamos tu información exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-35">
          <li data-id="element-36">Procesar y entregar tu compra (PDF e imprimibles)</li>
          <li data-id="element-37">Enviarte el enlace de descarga</li>
          <li data-id="element-38">Responder a consultas de soporte relacionadas con tu compra</li>
        </ul>
        <p data-id="element-39">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros con fines de marketing.
        </p>
      </div>

      <div className="space-y-4" data-id="element-40">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-41">3. Cookies</h3>
        <p data-id="element-42">
          Este sitio puede utilizar cookies esenciales para el funcionamiento
          básico del sitio web y el procesamiento de pagos. No utilizamos
          cookies de seguimiento publicitario.
        </p>
      </div>

      <div className="space-y-4" data-id="element-43">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-44">4. Seguridad</h3>
        <p data-id="element-45">
          Implementamos medidas de seguridad razonables para proteger tu
          información personal. Todas las transacciones se realizan a través de
          conexiones cifradas (SSL/TLS).
        </p>
      </div>

      <div className="space-y-4" data-id="element-46">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-47">5. Tus derechos</h3>
        <p data-id="element-48">
          Puedes solicitar acceso, corrección o eliminación de tus datos
          personales en cualquier momento. Para ejercer estos derechos,
          contáctanos a través de los canales indicados en tu correo de
          confirmación de compra.
        </p>
      </div>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-sm text-[#2D2D2D]/60" data-id="element-49">
        Última actualización: febrero 2026
      </p>

      <div className="space-y-4" data-id="element-50">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-51">1. Producto</h3>
        <p data-id="element-52">
          "El Club de las 3:00 AM" es un producto digital (PDF) con materiales
          imprimibles complementarios. Es un recurso educativo e informativo y{' '}
          <strong data-id="element-53">
            no constituye asesoramiento médico, diagnóstico ni tratamiento
          </strong>
          .
        </p>
      </div>

      <div className="space-y-4" data-id="element-54">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-55">
          2. Descargo de responsabilidad médica
        </h3>
        <p data-id="element-56">
          El contenido de este producto es únicamente informativo y educativo.
          No reemplaza la consulta con un profesional de la salud. Si tienes
          problemas de sueño severos o persistentes, consulta a tu médico. No
          garantizamos resultados específicos, ya que cada persona es diferente.
        </p>
      </div>

      <div className="space-y-4" data-id="element-57">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-58">
          3. Entrega y acceso
        </h3>
        <p data-id="element-59">
          Tras completar la compra, recibirás acceso inmediato al producto
          digital. El enlace de descarga se enviará a la dirección de correo
          electrónico proporcionada durante la compra.
        </p>
      </div>

      <div className="space-y-4" data-id="element-60">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-61">4. Uso personal</h3>
        <p data-id="element-62">
          El producto es para uso personal únicamente. No está permitido
          redistribuir, revender, copiar ni compartir el contenido del PDF ni
          los materiales imprimibles sin autorización expresa por escrito.
        </p>
      </div>

      <div className="space-y-4" data-id="element-63">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-64">
          5. Política de reembolso
        </h3>
        <p data-id="element-65">
          Debido a la naturaleza digital del producto (acceso inmediato), todas
          las ventas son finales. Si experimentas problemas técnicos para
          acceder al producto, contáctanos y te ayudaremos a resolverlo.
        </p>
      </div>

      <div className="space-y-4" data-id="element-66">
        <h3 className="font-serif text-xl text-[#1B2141]" data-id="element-67">
          6. Limitación de responsabilidad
        </h3>
        <p data-id="element-68">
          El Club de las 3:00 AM y sus creadores no se hacen responsables de
          ningún daño directo o indirecto derivado del uso de este producto. Al
          adquirir el producto, aceptas estos términos en su totalidad.
        </p>
      </div>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#1B2141] border-t border-[#FAF6F0]/10 py-8 px-6" data-id="element-69">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#FAF6F0]/40" data-id="element-70">
          <p data-id="element-71">
            © {new Date().getFullYear()} El Club de las 3:00 AM. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-4" data-id="element-72">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-[#C9A96E] transition-colors underline underline-offset-2" data-id="element-73">
              Privacidad
            </button>
            <span className="text-[#FAF6F0]/20" data-id="element-74">·</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-[#C9A96E] transition-colors underline underline-offset-2" data-id="element-75">
              Términos
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-76">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-77" />}
      </AnimatePresence>
    </>;
}